/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */

import {
  getPrimaryLanguage,
  IConfigurationModel,
  isPrimaryLanguage,
} from "@nf/common";
import React from "react";
import { useRouter } from "next/router";
import { NextSeo } from "next-seo";
import { useTranslation } from "next-i18next";
import {
  fallbackPageMetadata,
  PageMetadataKeys,
  pageMetaDataTranslationKeys,
} from "./pageMetadata";
import StructuredData from "./StructuredData";
import getOrganizationSchema from "schema/getOrganizationSchema";
import { RouteHelper } from "helpers/routeHelper";
import { useCountry, useLanguage } from "hooks";

interface ISeoProps {
  screenKey: PageMetadataKeys;
  configuration?: IConfigurationModel;
}

export const SEO = ({ screenKey, configuration }: ISeoProps) => {
  const { t } = useTranslation("translations");
  const router = useRouter();
  const country = useCountry();
  const language = useLanguage();

  const baseUrl = process.env.NEXT_PUBLIC_MAIN_DOMAIN ?? "";
  const href = baseUrl + router.asPath;

  const isHomePage = configuration?.Routes?.HOME.includes(screenKey);
  const canonicalUrl = isHomePage
    ? RouteHelper.getMainCanonicalUrl(country)
    : RouteHelper.getCountryCanonicalUrl(router.pathname, router.query);

  const title = t(
    pageMetaDataTranslationKeys?.[screenKey]?.title,
    fallbackPageMetadata.default.title
  );
  const description = t(
    pageMetaDataTranslationKeys?.[screenKey]?.description,
    fallbackPageMetadata.default.description
  );

  const primaryLanguage = getPrimaryLanguage(country);
  const primaryLanguageHref = isHomePage
    ? `${baseUrl}/${country}/${primaryLanguage}`
    : `${baseUrl}${RouteHelper.replacePathSegments(router.pathname, {
        ...router.query,
        country,
        language: primaryLanguage,
      })}`;

  // Non-primary language versions are noindexed, so hreflang must not
  // point to them - only the primary version is advertised.
  const hreflangAlternates = [
    {
      hrefLang: `${primaryLanguage}-${country.toUpperCase()}`,
      href: primaryLanguageHref,
    },
    {
      hrefLang: "x-default",
      href: primaryLanguageHref,
    },
  ];

  return (
    <>
      {isHomePage && <StructuredData schemas={[getOrganizationSchema()]} />}
      <NextSeo
        title={title}
        description={description}
        canonical={canonicalUrl}
        noindex={!isPrimaryLanguage(language, country)}
        languageAlternates={hreflangAlternates}
        openGraph={{
          title: title,
          description,
          url: href,
          site_name: process.env.NEXT_PUBLIC_NAME,
        }}
      />
    </>
  );
};

export default SEO;
